import type { SupabaseClient } from "@supabase/supabase-js";

import { isMissingPolicyRpc } from "./alert-channel-policy";

// Guard app access requests (design locks 2026-08-09, migration 20260812090000).
//
// A SecOps work login and an app store account are two different identities. This module is the
// thin wire over the six shipped RPCs: the tenant half (member.manage: request, correct while
// still requested, list own account) and the founder half (platform superadmin: cross-account
// queue, mark a store added by hand, close with a reason). SecOps never talks to the stores;
// the founder copies an address into App Store Connect or Play Console and records it here.
//
// Status machine (server-stored): requested -> part_added (both, one store done) -> added ->
// closed. "installed" is DERIVED server-side from a trusted Guard device checking in after the
// store was marked, and is the only state this surface may paint proof green: observed, not
// claimed. No store opt-in URL, ASC link, or Play link exists anywhere in this module or the
// surfaces that consume it; those are founder runbook material in Help and nowhere else.

export const REQUEST_GUARD_APP_ACCESS_RPC = "request_guard_app_access";
export const UPDATE_GUARD_APP_ACCESS_REQUEST_RPC = "update_guard_app_access_request";
export const LIST_GUARD_APP_ACCESS_REQUESTS_RPC = "list_guard_app_access_requests";
export const PLATFORM_LIST_GUARD_APP_ACCESS_REQUESTS_RPC = "platform_list_guard_app_access_requests";
export const PLATFORM_MARK_GUARD_APP_ACCESS_STORE_ADDED_RPC = "platform_mark_guard_app_access_store_added";
export const PLATFORM_CLOSE_GUARD_APP_ACCESS_REQUEST_RPC = "platform_close_guard_app_access_request";

export const GUARD_APP_PLATFORMS = ["ios", "android", "both"] as const;
export type GuardAppPlatform = (typeof GUARD_APP_PLATFORMS)[number];

export const GUARD_APP_REQUEST_STATUSES = ["requested", "part_added", "added", "closed"] as const;
export type GuardAppRequestStatus = (typeof GUARD_APP_REQUEST_STATUSES)[number];

export const GUARD_APP_CLOSE_REASONS = ["duplicate", "left_company", "wrong_address", "declined"] as const;
export type GuardAppCloseReason = (typeof GUARD_APP_CLOSE_REASONS)[number];

/** Founder close-reason chips, in the mock's order. */
export const GUARD_APP_CLOSE_REASON_COPY: Record<GuardAppCloseReason, string> = {
  duplicate: "Duplicate",
  left_company: "Left the company",
  wrong_address: "Wrong address",
  declined: "Declined",
};

export interface GuardAppAccessRequestRow {
  readonly requestId: string;
  readonly membershipId: string;
  readonly platform: GuardAppPlatform;
  readonly appleStoreEmail: string | null;
  readonly googleStoreEmail: string | null;
  readonly note: string | null;
  readonly status: GuardAppRequestStatus;
  /** Server-derived: a trusted Guard device checked in after its store was marked added. */
  readonly installed: boolean;
  readonly installedAppVersion: string | null;
  readonly installedSeenAt: string | null;
  readonly closedReason: GuardAppCloseReason | null;
  readonly requestedAt: string | null;
  readonly appleAddedAt: string | null;
  readonly googleAddedAt: string | null;
  readonly closedAt: string | null;
}

export type GuardAppAccessLoad =
  | { readonly status: "ready"; readonly rows: readonly GuardAppAccessRequestRow[] }
  | { readonly status: "unavailable" };

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asPlatform(value: unknown): GuardAppPlatform | null {
  return GUARD_APP_PLATFORMS.includes(value as GuardAppPlatform) ? (value as GuardAppPlatform) : null;
}

function asStatus(value: unknown): GuardAppRequestStatus | null {
  return GUARD_APP_REQUEST_STATUSES.includes(value as GuardAppRequestStatus)
    ? (value as GuardAppRequestStatus)
    : null;
}

function asCloseReason(value: unknown): GuardAppCloseReason | null {
  return GUARD_APP_CLOSE_REASONS.includes(value as GuardAppCloseReason)
    ? (value as GuardAppCloseReason)
    : null;
}

function mapGuardAppAccessRequestFields(
  row: Record<string, unknown>,
  membershipId: string,
): GuardAppAccessRequestRow | null {
  const requestId = asString(row.request_id);
  const platform = asPlatform(row.platform);
  const status = asStatus(row.status);
  if (requestId === null || platform === null || status === null) {
    return null;
  }
  return {
    requestId,
    membershipId,
    platform,
    appleStoreEmail: asString(row.apple_store_email),
    googleStoreEmail: asString(row.google_store_email),
    note: asString(row.note),
    status,
    installed: row.installed === true,
    installedAppVersion: asString(row.installed_app_version),
    installedSeenAt: asString(row.installed_seen_at),
    closedReason: asCloseReason(row.closed_reason),
    requestedAt: asString(row.requested_at),
    appleAddedAt: asString(row.apple_added_at),
    googleAddedAt: asString(row.google_added_at),
    closedAt: asString(row.closed_at),
  };
}

export function mapGuardAppAccessRequestRow(raw: unknown): GuardAppAccessRequestRow | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const membershipId = asString(row.membership_id);
  if (membershipId === null) return null;
  return mapGuardAppAccessRequestFields(row, membershipId);
}

/**
 * The account's own requests, newest first. A server without the RPC (console deployed ahead of
 * the hosted apply) reads as "unavailable": the directory then shows no Phone app column at all
 * rather than a column of statuses nothing can load.
 */
export async function fetchGuardAppAccessRequests(
  client: SupabaseClient,
  accountId: string,
): Promise<GuardAppAccessLoad> {
  const { data, error } = await client.rpc(LIST_GUARD_APP_ACCESS_REQUESTS_RPC, {
    p_account_id: accountId,
  });
  if (error !== null) {
    if (isMissingPolicyRpc(error)) {
      return { status: "unavailable" };
    }
    throw new Error("Could not load Guard app access requests.");
  }
  const rows: GuardAppAccessRequestRow[] = [];
  if (Array.isArray(data)) {
    for (const raw of data) {
      const row = mapGuardAppAccessRequestRow(raw);
      if (row !== null) rows.push(row);
    }
  }
  return { status: "ready", rows };
}

/**
 * The row that answers "where is this person with the Guard app": the open request when one
 * exists, else the most recent closed one (the reason stays visible), else null.
 */
export function relevantRequestForMember(
  rows: readonly GuardAppAccessRequestRow[],
  membershipId: string,
): GuardAppAccessRequestRow | null {
  const mine = rows.filter((row) => row.membershipId === membershipId);
  return mine.find((row) => row.status !== "closed") ?? mine[0] ?? null;
}

// ---------------------------------------------------------------------------
// Tenant status wording (design lock 3): the tenant sees a softer wording of the founder's
// five states. Requested covers requested AND part_added, because "half the stores are done"
// is founder workload, not something an admin can act on. Waiting names the store whose
// invite is actually out in the world. Installed is the only proof green. Closed shows why.
// ---------------------------------------------------------------------------

export type PhoneAppChipTone = "none" | "info" | "warn" | "proof" | "faint";

export interface PhoneAppChipLine {
  readonly tone: PhoneAppChipTone;
  readonly label: string;
}

export function phoneAppChipLines(row: GuardAppAccessRequestRow | null): readonly PhoneAppChipLine[] {
  if (row === null) {
    return [{ tone: "none", label: "Not requested" }];
  }
  if (row.status === "closed") {
    const reason = row.closedReason === null ? "" : `: ${GUARD_APP_CLOSE_REASON_COPY[row.closedReason]}`;
    return [{ tone: "faint", label: `Closed${reason}` }];
  }
  if (row.installed) {
    return [{ tone: "proof", label: "Installed" }];
  }
  if (row.status === "added") {
    // One line per invite that is out. A both request honestly has two waits.
    const lines: PhoneAppChipLine[] = [];
    if (row.appleAddedAt !== null) lines.push({ tone: "warn", label: "Waiting on Apple invite" });
    if (row.googleAddedAt !== null) lines.push({ tone: "warn", label: "Waiting on Google invite" });
    return lines;
  }
  return [{ tone: "info", label: "Requested" }];
}

// ---------------------------------------------------------------------------
// Draft validation. Fail closed BEFORE the wire, with the mock's sentences where the mock
// drew them; the server re-refuses everything regardless.
// ---------------------------------------------------------------------------

/** Mirrors the migration's shape check exactly. */
const STORE_EMAIL_SHAPE = /^[A-Z0-9._%+\-]+@[A-Z0-9.\-]+\.[A-Z]{2,}$/i;

export const GUARD_APP_NOTE_MAX = 500;

export interface GuardAppAccessDraft {
  readonly platform: GuardAppPlatform;
  readonly appleStoreEmail: string;
  readonly googleStoreEmail: string;
  readonly note: string;
}

export type GuardAppDraftValidation =
  | {
      readonly ok: true;
      readonly appleStoreEmail: string | null;
      readonly googleStoreEmail: string | null;
      readonly note: string | null;
    }
  | { readonly ok: false; readonly field: "apple" | "google" | "note"; readonly reason: string };

export function validateGuardAppAccessDraft(draft: GuardAppAccessDraft): GuardAppDraftValidation {
  // The never-requested store is dropped, never sent: an ios request carries no Google address.
  const apple = draft.platform === "android" ? "" : draft.appleStoreEmail.trim().toLowerCase();
  const google = draft.platform === "ios" ? "" : draft.googleStoreEmail.trim().toLowerCase();
  const note = draft.note.trim();

  if (draft.platform !== "android" && apple.length === 0) {
    return { ok: false, field: "apple", reason: "Add the Apple ID email before submitting." };
  }
  if (draft.platform === "both" && google.length === 0) {
    // The mock's exact blocker for the drawn invalid state.
    return {
      ok: false,
      field: "google",
      reason: "Add the Google account email before submitting, or change the phone to iPhone only.",
    };
  }
  if (draft.platform === "android" && google.length === 0) {
    return { ok: false, field: "google", reason: "Add the Google account email before submitting." };
  }
  if (apple.length > 0 && !STORE_EMAIL_SHAPE.test(apple)) {
    return { ok: false, field: "apple", reason: "That does not look like an email address." };
  }
  if (google.length > 0 && !STORE_EMAIL_SHAPE.test(google)) {
    return { ok: false, field: "google", reason: "That does not look like an email address." };
  }
  if (note.length > GUARD_APP_NOTE_MAX) {
    return { ok: false, field: "note", reason: "Keep the note under 500 characters." };
  }
  return {
    ok: true,
    appleStoreEmail: apple.length === 0 ? null : apple,
    googleStoreEmail: google.length === 0 ? null : google,
    note: note.length === 0 ? null : note,
  };
}

// ---------------------------------------------------------------------------
// Writes. Refusal allowlists are transcribed from migration 20260812090000; anything else,
// including the internal caller-mismatch guard, collapses to one safe sentence so the surface
// is never an error oracle.
// ---------------------------------------------------------------------------

export const SAFE_GUARD_APP_REQUEST_FAILURE = "Couldn't send that request - please try again.";

export const KNOWN_GUARD_APP_REQUEST_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to manage Guard app access requests.",
  "Account id is required to manage Guard app access requests.",
  "Member management permission is required.",
  "Membership was not found for this account.",
  "Membership must be active for a Guard app access request.",
  "Phone platform must be ios, android, or both.",
  "Apple ID email is required for this request.",
  "Google account email is required for this request.",
  "Apple ID email is not valid.",
  "Google account email is not valid.",
  "Request note is too long.",
  "An open Guard app access request already exists for this member.",
  "Guard app access request was not found for this account.",
  "Only a requested Guard app access request can be updated.",
]);

export type GuardAppWriteOutcome = { ok: true } | { ok: false; reason: string };

function tenantReason(error: unknown): string {
  const message =
    error !== null && typeof error === "object" && typeof (error as { message?: unknown }).message === "string"
      ? (error as { message: string }).message
      : "";
  return KNOWN_GUARD_APP_REQUEST_FAILURES.has(message) ? message : SAFE_GUARD_APP_REQUEST_FAILURE;
}

async function runTenantWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<GuardAppWriteOutcome> {
  try {
    const { error } = await client.rpc(fn, params);
    return error === null ? { ok: true } : { ok: false, reason: tenantReason(error) };
  } catch {
    return { ok: false, reason: SAFE_GUARD_APP_REQUEST_FAILURE };
  }
}

export async function requestGuardAppAccess(
  client: SupabaseClient,
  accountId: string,
  membershipId: string,
  payload: {
    platform: GuardAppPlatform;
    appleStoreEmail: string | null;
    googleStoreEmail: string | null;
    note: string | null;
  },
): Promise<GuardAppWriteOutcome> {
  return runTenantWrite(client, REQUEST_GUARD_APP_ACCESS_RPC, {
    p_account_id: accountId,
    p_membership_id: membershipId,
    p_platform: payload.platform,
    p_apple_store_email: payload.appleStoreEmail,
    p_google_store_email: payload.googleStoreEmail,
    p_note: payload.note,
  });
}

/** Correct a still-requested request. The server refuses this after any store mark. */
export async function updateGuardAppAccessRequest(
  client: SupabaseClient,
  accountId: string,
  requestId: string,
  payload: {
    platform: GuardAppPlatform;
    appleStoreEmail: string | null;
    googleStoreEmail: string | null;
    note: string | null;
  },
): Promise<GuardAppWriteOutcome> {
  return runTenantWrite(client, UPDATE_GUARD_APP_ACCESS_REQUEST_RPC, {
    p_account_id: accountId,
    p_request_id: requestId,
    p_platform: payload.platform,
    p_apple_store_email: payload.appleStoreEmail,
    p_google_store_email: payload.googleStoreEmail,
    p_note: payload.note,
  });
}

// ---------------------------------------------------------------------------
// Founder queue (platform superadmin only, /platform).
// ---------------------------------------------------------------------------

export interface PlatformGuardAppAccessRow extends GuardAppAccessRequestRow {
  readonly accountId: string;
  readonly accountName: string;
  readonly memberDisplayName: string | null;
  readonly memberEmail: string | null;
  readonly requestedByDisplayName: string | null;
  readonly requestedByRole: string | null;
  readonly appleAddedByUserId: string | null;
  readonly googleAddedByUserId: string | null;
  readonly closedByUserId: string | null;
}

export function mapPlatformGuardAppAccessRow(raw: unknown): PlatformGuardAppAccessRow | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const accountId = asString(row.account_id);
  if (accountId === null) return null;
  // The tenant list carries membership_id; the platform RPC identifies people by label only.
  const base = mapGuardAppAccessRequestFields(row, asString(row.membership_id) ?? "");
  if (base === null) return null;
  return {
    ...base,
    accountId,
    accountName: asString(row.account_name) ?? "Workspace",
    memberDisplayName: asString(row.member_display_name),
    memberEmail: asString(row.member_email),
    requestedByDisplayName: asString(row.requested_by_display_name),
    requestedByRole: asString(row.requested_by_role),
    appleAddedByUserId: asString(row.apple_added_by_user_id),
    googleAddedByUserId: asString(row.google_added_by_user_id),
    closedByUserId: asString(row.closed_by_user_id),
  };
}

/** Throws safe copy; the common denial is simply not holding the platform superadmin role. */
export async function fetchPlatformGuardAppAccessRequests(
  client: SupabaseClient,
): Promise<PlatformGuardAppAccessRow[]> {
  const { data, error } = await client.rpc(PLATFORM_LIST_GUARD_APP_ACCESS_REQUESTS_RPC);
  if (error !== null) {
    if (isMissingPolicyRpc(error)) {
      return [];
    }
    throw new Error("Could not load the access queue. Platform access is required for this view.");
  }
  const rows: PlatformGuardAppAccessRow[] = [];
  if (Array.isArray(data)) {
    for (const raw of data) {
      const row = mapPlatformGuardAppAccessRow(raw);
      if (row !== null) rows.push(row);
    }
  }
  return rows;
}

/** Requested plus part added: the rows with the founder at the end of them. Drives the tab count. */
export function actionableQueueCount(rows: readonly PlatformGuardAppAccessRow[]): number {
  return rows.filter((row) => row.status === "requested" || row.status === "part_added").length;
}

export type QueueFilter = "mine" | "ios" | "android" | "all";

export function applyQueueFilter(
  rows: readonly PlatformGuardAppAccessRow[],
  filter: QueueFilter,
): PlatformGuardAppAccessRow[] {
  return rows.filter((row) => {
    switch (filter) {
      case "mine":
        return row.status === "requested" || row.status === "part_added";
      case "ios":
        return row.platform === "ios" || row.platform === "both";
      case "android":
        return row.platform === "android" || row.platform === "both";
      case "all":
        return true;
    }
  });
}

export interface QueueChip {
  readonly tone: PhoneAppChipTone;
  readonly label: string;
}

/** Founder wording of the five states. Green marks Installed only: nothing left to do. */
export function queueStatusChip(row: PlatformGuardAppAccessRow): QueueChip {
  if (row.status === "closed") return { tone: "faint", label: "Closed" };
  if (row.installed) return { tone: "proof", label: "Installed" };
  if (row.status === "added") return { tone: "faint", label: "Added" };
  if (row.status === "part_added") return { tone: "warn", label: "Part added" };
  return { tone: "info", label: "Requested" };
}

export const SAFE_GUARD_APP_PLATFORM_FAILURE = "Couldn't record that - please try again.";

export const KNOWN_GUARD_APP_PLATFORM_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to manage Guard app access requests.",
  "Platform superadmin is required for the Guard app access queue.",
  "Guard app access request was not found.",
  "Store must be apple or google.",
  "Apple store is not part of this request.",
  "Google store is not part of this request.",
  "This store is already marked added.",
  "A closed Guard app access request cannot be changed.",
  "Close reason must be duplicate, left_company, wrong_address, or declined.",
  "Guard app access request is already closed.",
]);

function platformReason(error: unknown): string {
  const message =
    error !== null && typeof error === "object" && typeof (error as { message?: unknown }).message === "string"
      ? (error as { message: string }).message
      : "";
  return KNOWN_GUARD_APP_PLATFORM_FAILURES.has(message) ? message : SAFE_GUARD_APP_PLATFORM_FAILURE;
}

async function runPlatformWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<GuardAppWriteOutcome> {
  try {
    const { error } = await client.rpc(fn, params);
    return error === null ? { ok: true } : { ok: false, reason: platformReason(error) };
  } catch {
    return { ok: false, reason: SAFE_GUARD_APP_PLATFORM_FAILURE };
  }
}

/** Record that YOU added this address by hand in the store console. A claim, so never bulk. */
export async function markGuardAppStoreAdded(
  client: SupabaseClient,
  requestId: string,
  store: "apple" | "google",
): Promise<GuardAppWriteOutcome> {
  return runPlatformWrite(client, PLATFORM_MARK_GUARD_APP_ACCESS_STORE_ADDED_RPC, {
    p_request_id: requestId,
    p_store: store,
  });
}

export async function closeGuardAppAccessRequest(
  client: SupabaseClient,
  requestId: string,
  reason: GuardAppCloseReason,
): Promise<GuardAppWriteOutcome> {
  return runPlatformWrite(client, PLATFORM_CLOSE_GUARD_APP_ACCESS_REQUEST_RPC, {
    p_request_id: requestId,
    p_reason: reason,
  });
}
